/**
 * OpenAI Setup Tutorial Page
 * Includes API key verification tool
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useI18n, Locale } from '@/lib/i18n';
import { localizePath } from '@/lib/locale-path';
import { useDocumentMeta } from '@/lib/document-meta';
import { Lightbox } from '@/components/docs/Lightbox';
import './tutorials.scss';

interface Translation {
  pageTitle: string;
  backLink: string;
  overview: { title: string; content: string };
  requirements: { title: string; items: string[] };
  apiKeyVerifier: {
    title: string;
    description: string;
    inputLabel: string;
    inputPlaceholder: string;
    verifyButton: string;
    clearButton: string;
    verifying: string;
    results: {
      title: string;
      valid: string;
      invalid: string;
      realtimeAccess: string;
      whisperAccess: string;
      checking: string;
    };
    summary: {
      ready: string;
      notReady: string;
    };
  };
  steps: {
    createAccount: { title: string; content: string };
    getApiKey: { title: string; content: string };
    addCredits: { title: string; content: string };
    configureInApp: { title: string; content: string };
  };
  tips: { title: string; items: string[] };
  troubleshooting: { title: string; content: string };
}

const translations: Record<Locale, Translation> = {
  en: {
    pageTitle: 'OpenAI Setup Tutorial',
    backLink: 'Back to AI Providers',
    overview: {
      title: 'Overview',
      content: 'This guide will help you set up OpenAI for use with Sokuji, which uses the Realtime API for speech-to-speech translation.',
    },
    requirements: {
      title: 'Requirements',
      items: [
        'OpenAI account (create one at platform.openai.com)',
        'Payment method added to your OpenAI account',
        'API credits (minimum $5 recommended)',
        'Realtime API access (included with paid accounts)',
      ],
    },
    apiKeyVerifier: {
      title: 'API Key Verification Tool',
      description: 'Paste your OpenAI API key below to verify it has the required permissions for Sokuji.',
      inputLabel: 'OpenAI API Key',
      inputPlaceholder: 'sk-...',
      verifyButton: 'Verify API Key',
      clearButton: 'Clear',
      verifying: 'Verifying...',
      results: {
        title: 'Verification Results',
        valid: 'API Key is valid',
        invalid: 'API Key is invalid',
        realtimeAccess: 'Realtime API access',
        whisperAccess: 'Whisper API access',
        checking: 'Checking...',
      },
      summary: {
        ready: 'Your API key is ready to use with Sokuji!',
        notReady: 'Your API key needs additional permissions or credits.',
      },
    },
    steps: {
      createAccount: {
        title: 'Step 1: Create an OpenAI Account',
        content: 'Go to <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> and sign up for an account. You can use your email or sign in with Google/Microsoft.',
      },
      getApiKey: {
        title: 'Step 2: Generate an API Key',
        content: 'Navigate to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a> in your OpenAI dashboard. Click "Create new secret key", give it a name (e.g., "Sokuji"), and copy the key. Make sure to save it securely as you won\'t be able to see it again.',
      },
      addCredits: {
        title: 'Step 3: Add Credits to Your Account',
        content: 'Go to <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">Billing</a> and add a payment method. We recommend adding at least $5-10 in credits to start. The Realtime API costs approximately $0.06 per minute of audio.',
      },
      configureInApp: {
        title: 'Step 4: Configure in Sokuji',
        content: 'Open Sokuji and go to Settings. Select "OpenAI" as your AI provider and paste your API key in the API Key field. Click "Verify" to ensure the key is working correctly.',
      },
    },
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Monitor your usage in the OpenAI dashboard to avoid unexpected charges',
        'Set up usage limits in your OpenAI account settings',
        'The Realtime API provides the best quality for real-time translation',
        'Keep your API key secure and never share it publicly',
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content: 'If you encounter issues, check your API key permissions, ensure you have sufficient credits, and verify Realtime API access is enabled. Visit <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">OpenAI Documentation</a> for more help.',
    },
  },
  zh: {
    pageTitle: 'OpenAI 设置教程',
    backLink: '返回 AI 提供商',
    overview: {
      title: '概述',
      content: '本指南将帮助您设置 OpenAI 以与 Sokuji 配合使用。OpenAI 提供强大的 AI 模型，包括用于实时语音翻译的 Realtime API。',
    },
    requirements: {
      title: '要求',
      items: [
        'OpenAI 账户（在 platform.openai.com 创建）',
        '在您的 OpenAI 账户中添加付款方式',
        'API 额度（建议至少 $5）',
        'Realtime API 访问权限（付费账户包含）',
      ],
    },
    apiKeyVerifier: {
      title: 'API Key 验证工具',
      description: '将您的 OpenAI API Key 粘贴到下方，以验证它是否具有 Sokuji 所需的权限。',
      inputLabel: 'OpenAI API Key',
      inputPlaceholder: 'sk-...',
      verifyButton: '验证 API Key',
      clearButton: '清除',
      verifying: '验证中...',
      results: {
        title: '验证结果',
        valid: 'API Key 有效',
        invalid: 'API Key 无效',
        realtimeAccess: 'Realtime API 访问权限',
        whisperAccess: 'Whisper API 访问权限',
        checking: '检查中...',
      },
      summary: {
        ready: '您的 API Key 已准备好在 Sokuji 中使用！',
        notReady: '您的 API Key 需要额外的权限或额度。',
      },
    },
    steps: {
      createAccount: {
        title: '步骤 1：创建 OpenAI 账户',
        content: '访问 <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> 并注册账户。您可以使用电子邮件或通过 Google/Microsoft 登录。',
      },
      getApiKey: {
        title: '步骤 2：生成 API Key',
        content: '在 OpenAI 仪表板中导航到 <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a>。点击"创建新密钥"，为其命名（例如"Sokuji"），然后复制密钥。请确保安全保存，因为您将无法再次查看它。',
      },
      addCredits: {
        title: '步骤 3：向账户添加额度',
        content: '访问 <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">账单</a> 并添加付款方式。我们建议开始时至少添加 $5-10 的额度。Realtime API 每分钟音频大约花费 $0.06。',
      },
      configureInApp: {
        title: '步骤 4：在 Sokuji 中配置',
        content: '打开 Sokuji 并进入设置。选择"OpenAI"作为您的 AI 提供商，并在 API Key 字段中粘贴您的密钥。点击"验证"以确保密钥正常工作。',
      },
    },
    tips: {
      title: '最佳使用建议',
      items: [
        '在 OpenAI 仪表板中监控您的使用情况，以避免意外费用',
        '在 OpenAI 账户设置中设置使用限制',
        'Realtime API 为实时翻译提供最佳质量',
        '保护您的 API Key 安全，切勿公开分享',
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content: '如果遇到问题，请检查您的 API Key 权限，确保有足够的额度，并验证 Realtime API 访问已启用。访问 <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">OpenAI 文档</a> 获取更多帮助。',
    },
  },
  ja: {
    pageTitle: 'OpenAI セットアップチュートリアル',
    backLink: 'AI プロバイダーに戻る',
    overview: {
      title: '概要',
      content: 'このガイドでは、Sokuji で使用するための OpenAI のセットアップを説明します。OpenAI は、リアルタイム音声翻訳用の Realtime API を含む強力な AI モデルを提供しています。',
    },
    requirements: {
      title: '要件',
      items: [
        'OpenAI アカウント（platform.openai.com で作成）',
        'OpenAI アカウントに支払い方法を追加',
        'API クレジット（最低 $5 推奨）',
        'Realtime API アクセス（有料アカウントに含まれる）',
      ],
    },
    apiKeyVerifier: {
      title: 'API キー検証ツール',
      description: 'OpenAI API キーを以下に貼り付けて、Sokuji に必要な権限があるか確認します。',
      inputLabel: 'OpenAI API キー',
      inputPlaceholder: 'sk-...',
      verifyButton: 'API キーを検証',
      clearButton: 'クリア',
      verifying: '検証中...',
      results: {
        title: '検証結果',
        valid: 'API キーは有効です',
        invalid: 'API キーは無効です',
        realtimeAccess: 'Realtime API アクセス',
        whisperAccess: 'Whisper API アクセス',
        checking: '確認中...',
      },
      summary: {
        ready: 'API キーは Sokuji で使用する準備ができています！',
        notReady: 'API キーには追加の権限またはクレジットが必要です。',
      },
    },
    steps: {
      createAccount: {
        title: 'ステップ 1: OpenAI アカウントを作成',
        content: '<a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> にアクセスしてアカウントを登録します。メールまたは Google/Microsoft でサインインできます。',
      },
      getApiKey: {
        title: 'ステップ 2: API キーを生成',
        content: 'OpenAI ダッシュボードで <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API キー</a> に移動します。「新しいシークレットキーを作成」をクリックし、名前を付けて（例：「Sokuji」）、キーをコピーします。再度表示できないため、安全に保存してください。',
      },
      addCredits: {
        title: 'ステップ 3: アカウントにクレジットを追加',
        content: '<a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">請求</a> にアクセスして支払い方法を追加します。開始時には少なくとも $5-10 のクレジットを追加することをお勧めします。Realtime API は音声 1 分あたり約 $0.06 かかります。',
      },
      configureInApp: {
        title: 'ステップ 4: Sokuji で設定',
        content: 'Sokuji を開いて設定に移動します。AI プロバイダーとして「OpenAI」を選択し、API キーフィールドにキーを貼り付けます。「検証」をクリックしてキーが正常に動作することを確認します。',
      },
    },
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        '予期しない請求を避けるために OpenAI ダッシュボードで使用状況を監視する',
        'OpenAI アカウント設定で使用制限を設定する',
        'Realtime API はリアルタイム翻訳に最高の品質を提供します',
        'API キーを安全に保ち、公開しないでください',
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content: '問題が発生した場合は、API キーの権限を確認し、十分なクレジットがあることを確認し、Realtime API アクセスが有効になっていることを確認してください。詳細は <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">OpenAI ドキュメント</a> を参照してください。',
    },
  },
  ko: {
    pageTitle: 'OpenAI 설정 튜토리얼',
    backLink: 'AI 제공업체로 돌아가기',
    overview: {
      title: '개요',
      content: '이 가이드는 Sokuji와 함께 사용하기 위한 OpenAI 설정을 도와드립니다. OpenAI는 실시간 음성 번역을 위한 Realtime API를 포함한 강력한 AI 모델을 제공합니다.',
    },
    requirements: {
      title: '요구사항',
      items: [
        'OpenAI 계정 (platform.openai.com에서 생성)',
        'OpenAI 계정에 결제 방법 추가',
        'API 크레딧 (최소 $5 권장)',
        'Realtime API 액세스 (유료 계정에 포함)',
      ],
    },
    apiKeyVerifier: {
      title: 'API 키 확인 도구',
      description: 'OpenAI API 키를 아래에 붙여넣어 Sokuji에 필요한 권한이 있는지 확인하세요.',
      inputLabel: 'OpenAI API 키',
      inputPlaceholder: 'sk-...',
      verifyButton: 'API 키 확인',
      clearButton: '지우기',
      verifying: '확인 중...',
      results: {
        title: '확인 결과',
        valid: 'API 키가 유효합니다',
        invalid: 'API 키가 유효하지 않습니다',
        realtimeAccess: 'Realtime API 액세스',
        whisperAccess: 'Whisper API 액세스',
        checking: '확인 중...',
      },
      summary: {
        ready: 'API 키가 Sokuji에서 사용할 준비가 되었습니다!',
        notReady: 'API 키에 추가 권한 또는 크레딧이 필요합니다.',
      },
    },
    steps: {
      createAccount: {
        title: '1단계: OpenAI 계정 만들기',
        content: '<a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a>에 접속하여 계정을 등록하세요. 이메일 또는 Google/Microsoft로 로그인할 수 있습니다.',
      },
      getApiKey: {
        title: '2단계: API 키 생성',
        content: 'OpenAI 대시보드에서 <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API 키</a>로 이동합니다. "새 비밀 키 만들기"를 클릭하고 이름을 지정한 다음(예: "Sokuji") 키를 복사합니다. 다시 볼 수 없으므로 안전하게 저장하세요.',
      },
      addCredits: {
        title: '3단계: 계정에 크레딧 추가',
        content: '<a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">결제</a>로 이동하여 결제 방법을 추가합니다. 시작 시 최소 $5-10의 크레딧을 추가하는 것이 좋습니다. Realtime API는 오디오 1분당 약 $0.06입니다.',
      },
      configureInApp: {
        title: '4단계: Sokuji에서 구성',
        content: 'Sokuji를 열고 설정으로 이동합니다. AI 제공업체로 "OpenAI"를 선택하고 API 키 필드에 키를 붙여넣습니다. "확인"을 클릭하여 키가 올바르게 작동하는지 확인합니다.',
      },
    },
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '예상치 못한 요금을 피하기 위해 OpenAI 대시보드에서 사용량 모니터링',
        'OpenAI 계정 설정에서 사용 한도 설정',
        'Realtime API는 실시간 번역에 최고의 품질을 제공합니다',
        'API 키를 안전하게 보관하고 공개적으로 공유하지 마세요',
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content: '문제가 발생하면 API 키 권한을 확인하고, 충분한 크레딧이 있는지 확인하고, Realtime API 액세스가 활성화되어 있는지 확인하세요. 자세한 내용은 <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">OpenAI 문서</a>를 참조하세요.',
    },
  },
  es: {
    pageTitle: 'Tutorial de configuración de OpenAI',
    backLink: 'Volver a proveedores de IA',
    overview: {
      title: 'Descripción general',
      content: 'Esta guía le ayudará a configurar OpenAI para usarlo con Sokuji. OpenAI ofrece potentes modelos de IA, incluida la Realtime API para la traducción de voz en tiempo real.',
    },
    requirements: {
      title: 'Requisitos',
      items: [
        'Cuenta de OpenAI (cree una en platform.openai.com)',
        'Método de pago agregado a su cuenta de OpenAI',
        'Créditos de API (se recomiendan al menos $5)',
        'Acceso a la Realtime API (incluido con cuentas de pago)',
      ],
    },
    apiKeyVerifier: {
      title: 'Herramienta de verificación de API Key',
      description: 'Pegue su API Key de OpenAI a continuación para verificar que tiene los permisos necesarios para Sokuji.',
      inputLabel: 'OpenAI API Key',
      inputPlaceholder: 'sk-...',
      verifyButton: 'Verificar API Key',
      clearButton: 'Borrar',
      verifying: 'Verificando...',
      results: {
        title: 'Resultados de la verificación',
        valid: 'La API Key es válida',
        invalid: 'La API Key no es válida',
        realtimeAccess: 'Acceso a Realtime API',
        whisperAccess: 'Acceso a Whisper API',
        checking: 'Verificando...',
      },
      summary: {
        ready: '¡Su API Key está lista para usarse con Sokuji!',
        notReady: 'Su API Key necesita permisos o créditos adicionales.',
      },
    },
    steps: {
      createAccount: {
        title: 'Paso 1: Crear una cuenta de OpenAI',
        content: 'Vaya a <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> y regístrese para obtener una cuenta. Puede usar su correo electrónico o iniciar sesión con Google/Microsoft.',
      },
      getApiKey: {
        title: 'Paso 2: Generar una API Key',
        content: 'Navegue a <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a> en su panel de OpenAI. Haga clic en "Crear nueva clave secreta", asígnele un nombre (por ejemplo, "Sokuji") y copie la clave. Asegúrese de guardarla de forma segura, ya que no podrá verla de nuevo.',
      },
      addCredits: {
        title: 'Paso 3: Agregar créditos a su cuenta',
        content: 'Vaya a <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">Facturación</a> y agregue un método de pago. Recomendamos agregar al menos $5-10 en créditos para comenzar. La Realtime API cuesta aproximadamente $0.06 por minuto de audio.',
      },
      configureInApp: {
        title: 'Paso 4: Configurar en Sokuji',
        content: 'Abra Sokuji y vaya a Configuración. Seleccione "OpenAI" como su proveedor de IA y pegue su API Key en el campo correspondiente. Haga clic en "Verificar" para asegurarse de que la clave funciona correctamente.',
      },
    },
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Supervise su uso en el panel de OpenAI para evitar cargos inesperados',
        'Configure límites de uso en la configuración de su cuenta de OpenAI',
        'La Realtime API proporciona la mejor calidad para la traducción en tiempo real',
        'Mantenga su API Key segura y nunca la comparta públicamente',
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      content: 'Si encuentra problemas, verifique los permisos de su API Key, asegúrese de tener suficientes créditos y verifique que el acceso a la Realtime API esté habilitado. Visite <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">la documentación de OpenAI</a> para obtener más ayuda.',
    },
  },
  fr: {
    pageTitle: 'Tutoriel de configuration OpenAI',
    backLink: 'Retour aux fournisseurs d\'IA',
    overview: {
      title: 'Aperçu',
      content: 'Ce guide vous aidera à configurer OpenAI pour l\'utiliser avec Sokuji. OpenAI fournit des modèles d\'IA puissants, y compris la Realtime API pour la traduction vocale en temps réel.',
    },
    requirements: {
      title: 'Prérequis',
      items: [
        'Compte OpenAI (créez-en un sur platform.openai.com)',
        'Moyen de paiement ajouté à votre compte OpenAI',
        'Crédits API (minimum de 5 $ recommandé)',
        'Accès à la Realtime API (inclus avec les comptes payants)',
      ],
    },
    apiKeyVerifier: {
      title: 'Outil de vérification de la clé API',
      description: 'Collez votre clé API OpenAI ci-dessous pour vérifier qu\'elle dispose des autorisations requises pour Sokuji.',
      inputLabel: 'Clé API OpenAI',
      inputPlaceholder: 'sk-...',
      verifyButton: 'Vérifier la clé API',
      clearButton: 'Effacer',
      verifying: 'Vérification...',
      results: {
        title: 'Résultats de la vérification',
        valid: 'La clé API est valide',
        invalid: 'La clé API est invalide',
        realtimeAccess: 'Accès à la Realtime API',
        whisperAccess: 'Accès à la Whisper API',
        checking: 'Vérification...',
      },
      summary: {
        ready: 'Votre clé API est prête à être utilisée avec Sokuji !',
        notReady: 'Votre clé API nécessite des autorisations ou des crédits supplémentaires.',
      },
    },
    steps: {
      createAccount: {
        title: 'Étape 1 : Créer un compte OpenAI',
        content: 'Rendez-vous sur <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> et inscrivez-vous. Vous pouvez utiliser votre e-mail ou vous connecter avec Google/Microsoft.',
      },
      getApiKey: {
        title: 'Étape 2 : Générer une clé API',
        content: 'Accédez aux <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">clés API</a> dans votre tableau de bord OpenAI. Cliquez sur « Créer une nouvelle clé secrète », donnez-lui un nom (par exemple « Sokuji ») et copiez la clé. Veillez à la sauvegarder en lieu sûr car vous ne pourrez plus la revoir.',
      },
      addCredits: {
        title: 'Étape 3 : Ajouter des crédits à votre compte',
        content: 'Accédez à la <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">facturation</a> et ajoutez un moyen de paiement. Nous recommandons d\'ajouter au moins 5 à 10 $ de crédits pour commencer. La Realtime API coûte environ 0,06 $ par minute d\'audio.',
      },
      configureInApp: {
        title: 'Étape 4 : Configurer dans Sokuji',
        content: 'Ouvrez Sokuji et accédez aux paramètres. Sélectionnez « OpenAI » comme fournisseur d\'IA et collez votre clé API dans le champ prévu. Cliquez sur « Vérifier » pour vous assurer que la clé fonctionne correctement.',
      },
    },
    tips: {
      title: 'Conseils pour de meilleurs résultats',
      items: [
        'Surveillez votre utilisation dans le tableau de bord OpenAI pour éviter les frais inattendus',
        'Configurez des limites d\'utilisation dans les paramètres de votre compte OpenAI',
        'La Realtime API offre la meilleure qualité pour la traduction en temps réel',
        'Gardez votre clé API en sécurité et ne la partagez jamais publiquement',
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      content: 'Si vous rencontrez des problèmes, vérifiez les autorisations de votre clé API, assurez-vous d\'avoir suffisamment de crédits et vérifiez que l\'accès à la Realtime API est activé. Consultez la <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">documentation OpenAI</a> pour plus d\'aide.',
    },
  },
  ar: {
    pageTitle: 'دليل إعداد OpenAI',
    backLink: 'العودة إلى مزودي الذكاء الاصطناعي',
    overview: {
      title: 'نظرة عامة',
      content: 'سيساعدك هذا الدليل على إعداد OpenAI للاستخدام مع Sokuji. يوفر OpenAI نماذج ذكاء اصطناعي قوية بما في ذلك Realtime API للترجمة الصوتية في الوقت الفعلي.',
    },
    requirements: {
      title: 'المتطلبات',
      items: [
        'حساب OpenAI (أنشئ واحدًا على platform.openai.com)',
        'طريقة دفع مضافة إلى حساب OpenAI الخاص بك',
        'رصيد API (يُنصح بحد أدنى 5 دولارات)',
        'الوصول إلى Realtime API (مشمول مع الحسابات المدفوعة)',
      ],
    },
    apiKeyVerifier: {
      title: 'أداة التحقق من مفتاح API',
      description: 'الصق مفتاح API الخاص بـ OpenAI أدناه للتحقق من أنه يمتلك الأذونات المطلوبة لـ Sokuji.',
      inputLabel: 'مفتاح OpenAI API',
      inputPlaceholder: 'sk-...',
      verifyButton: 'التحقق من مفتاح API',
      clearButton: 'مسح',
      verifying: 'جارٍ التحقق...',
      results: {
        title: 'نتائج التحقق',
        valid: 'مفتاح API صالح',
        invalid: 'مفتاح API غير صالح',
        realtimeAccess: 'الوصول إلى Realtime API',
        whisperAccess: 'الوصول إلى Whisper API',
        checking: 'جارٍ التحقق...',
      },
      summary: {
        ready: 'مفتاح API الخاص بك جاهز للاستخدام مع Sokuji!',
        notReady: 'يحتاج مفتاح API الخاص بك إلى أذونات أو رصيد إضافي.',
      },
    },
    steps: {
      createAccount: {
        title: 'الخطوة 1: إنشاء حساب OpenAI',
        content: 'انتقل إلى <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> وسجّل للحصول على حساب. يمكنك استخدام بريدك الإلكتروني أو تسجيل الدخول عبر Google/Microsoft.',
      },
      getApiKey: {
        title: 'الخطوة 2: إنشاء مفتاح API',
        content: 'انتقل إلى <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">مفاتيح API</a> في لوحة تحكم OpenAI. انقر على "إنشاء مفتاح سري جديد"، وأعطه اسمًا (مثل "Sokuji")، وانسخ المفتاح. تأكد من حفظه بشكل آمن لأنك لن تتمكن من رؤيته مرة أخرى.',
      },
      addCredits: {
        title: 'الخطوة 3: إضافة رصيد إلى حسابك',
        content: 'انتقل إلى <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">الفواتير</a> وأضف طريقة دفع. نوصي بإضافة ما لا يقل عن 5-10 دولارات من الرصيد للبدء. تكلف Realtime API حوالي 0.06 دولار لكل دقيقة من الصوت.',
      },
      configureInApp: {
        title: 'الخطوة 4: التكوين في Sokuji',
        content: 'افتح Sokuji وانتقل إلى الإعدادات. حدد "OpenAI" كمزود الذكاء الاصطناعي الخاص بك والصق مفتاح API في الحقل المخصص. انقر على "تحقق" للتأكد من أن المفتاح يعمل بشكل صحيح.',
      },
    },
    tips: {
      title: 'نصائح للحصول على أفضل النتائج',
      items: [
        'راقب استخدامك في لوحة تحكم OpenAI لتجنب الرسوم غير المتوقعة',
        'قم بتعيين حدود الاستخدام في إعدادات حساب OpenAI الخاص بك',
        'توفر Realtime API أفضل جودة للترجمة في الوقت الفعلي',
        'حافظ على أمان مفتاح API الخاص بك ولا تشاركه علنًا أبدًا',
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      content: 'إذا واجهت مشكلات، تحقق من أذونات مفتاح API الخاص بك، وتأكد من وجود رصيد كافٍ، وتحقق من تفعيل الوصول إلى Realtime API. قم بزيارة <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">وثائق OpenAI</a> للحصول على مزيد من المساعدة.',
    },
  },
  pt: {
    pageTitle: 'Tutorial de configuração do OpenAI',
    backLink: 'Voltar para provedores de IA',
    overview: {
      title: 'Visão geral',
      content: 'Este guia ajudará você a configurar o OpenAI para uso com o Sokuji. O OpenAI fornece modelos de IA poderosos, incluindo a Realtime API para tradução de voz em tempo real.',
    },
    requirements: {
      title: 'Requisitos',
      items: [
        'Conta OpenAI (crie uma em platform.openai.com)',
        'Método de pagamento adicionado à sua conta OpenAI',
        'Créditos de API (mínimo de $5 recomendado)',
        'Acesso à Realtime API (incluído com contas pagas)',
      ],
    },
    apiKeyVerifier: {
      title: 'Ferramenta de verificação de API Key',
      description: 'Cole sua API Key do OpenAI abaixo para verificar se ela possui as permissões necessárias para o Sokuji.',
      inputLabel: 'API Key do OpenAI',
      inputPlaceholder: 'sk-...',
      verifyButton: 'Verificar API Key',
      clearButton: 'Limpar',
      verifying: 'Verificando...',
      results: {
        title: 'Resultados da verificação',
        valid: 'A API Key é válida',
        invalid: 'A API Key é inválida',
        realtimeAccess: 'Acesso à Realtime API',
        whisperAccess: 'Acesso à Whisper API',
        checking: 'Verificando...',
      },
      summary: {
        ready: 'Sua API Key está pronta para uso com o Sokuji!',
        notReady: 'Sua API Key precisa de permissões ou créditos adicionais.',
      },
    },
    steps: {
      createAccount: {
        title: 'Passo 1: Criar uma conta OpenAI',
        content: 'Acesse <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> e registre-se para obter uma conta. Você pode usar seu e-mail ou entrar com Google/Microsoft.',
      },
      getApiKey: {
        title: 'Passo 2: Gerar uma API Key',
        content: 'Navegue até <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a> no seu painel do OpenAI. Clique em "Criar nova chave secreta", dê um nome (por exemplo, "Sokuji") e copie a chave. Certifique-se de salvá-la com segurança, pois você não poderá vê-la novamente.',
      },
      addCredits: {
        title: 'Passo 3: Adicionar créditos à sua conta',
        content: 'Acesse <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">Faturamento</a> e adicione um método de pagamento. Recomendamos adicionar pelo menos $5-10 em créditos para começar. A Realtime API custa aproximadamente $0,06 por minuto de áudio.',
      },
      configureInApp: {
        title: 'Passo 4: Configurar no Sokuji',
        content: 'Abra o Sokuji e vá para Configurações. Selecione "OpenAI" como seu provedor de IA e cole sua API Key no campo correspondente. Clique em "Verificar" para garantir que a chave está funcionando corretamente.',
      },
    },
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Monitore seu uso no painel do OpenAI para evitar cobranças inesperadas',
        'Configure limites de uso nas configurações da sua conta OpenAI',
        'A Realtime API oferece a melhor qualidade para tradução em tempo real',
        'Mantenha sua API Key segura e nunca a compartilhe publicamente',
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      content: 'Se encontrar problemas, verifique as permissões da sua API Key, certifique-se de ter créditos suficientes e verifique se o acesso à Realtime API está habilitado. Visite a <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">documentação do OpenAI</a> para mais ajuda.',
    },
  },
  ru: {
    pageTitle: 'Руководство по настройке OpenAI',
    backLink: 'Назад к провайдерам ИИ',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить OpenAI для использования с Sokuji. OpenAI предоставляет мощные модели ИИ, включая Realtime API для голосового перевода в реальном времени.',
    },
    requirements: {
      title: 'Требования',
      items: [
        'Аккаунт OpenAI (создайте на platform.openai.com)',
        'Способ оплаты, добавленный в ваш аккаунт OpenAI',
        'Кредиты API (рекомендуется минимум $5)',
        'Доступ к Realtime API (включён в платные аккаунты)',
      ],
    },
    apiKeyVerifier: {
      title: 'Инструмент проверки API-ключа',
      description: 'Вставьте ваш API-ключ OpenAI ниже, чтобы проверить наличие необходимых разрешений для Sokuji.',
      inputLabel: 'API-ключ OpenAI',
      inputPlaceholder: 'sk-...',
      verifyButton: 'Проверить API-ключ',
      clearButton: 'Очистить',
      verifying: 'Проверка...',
      results: {
        title: 'Результаты проверки',
        valid: 'API-ключ действителен',
        invalid: 'API-ключ недействителен',
        realtimeAccess: 'Доступ к Realtime API',
        whisperAccess: 'Доступ к Whisper API',
        checking: 'Проверка...',
      },
      summary: {
        ready: 'Ваш API-ключ готов к использованию с Sokuji!',
        notReady: 'Вашему API-ключу требуются дополнительные разрешения или кредиты.',
      },
    },
    steps: {
      createAccount: {
        title: 'Шаг 1: Создайте аккаунт OpenAI',
        content: 'Перейдите на <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> и зарегистрируйте аккаунт. Вы можете использовать электронную почту или войти через Google/Microsoft.',
      },
      getApiKey: {
        title: 'Шаг 2: Сгенерируйте API-ключ',
        content: 'Перейдите в раздел <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a> в панели управления OpenAI. Нажмите «Создать новый секретный ключ», дайте ему имя (например, «Sokuji») и скопируйте ключ. Обязательно сохраните его в безопасном месте, так как вы не сможете увидеть его снова.',
      },
      addCredits: {
        title: 'Шаг 3: Пополните баланс',
        content: 'Перейдите в раздел <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">Биллинг</a> и добавьте способ оплаты. Рекомендуем пополнить баланс минимум на $5-10. Стоимость Realtime API составляет примерно $0,06 за минуту аудио.',
      },
      configureInApp: {
        title: 'Шаг 4: Настройка в Sokuji',
        content: 'Откройте Sokuji и перейдите в настройки. Выберите «OpenAI» в качестве провайдера ИИ и вставьте API-ключ в соответствующее поле. Нажмите «Проверить», чтобы убедиться, что ключ работает корректно.',
      },
    },
    tips: {
      title: 'Советы для лучших результатов',
      items: [
        'Отслеживайте использование в панели управления OpenAI, чтобы избежать неожиданных расходов',
        'Установите лимиты использования в настройках аккаунта OpenAI',
        'Realtime API обеспечивает лучшее качество перевода в реальном времени',
        'Храните API-ключ в безопасности и никогда не делитесь им публично',
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      content: 'При возникновении проблем проверьте разрешения API-ключа, убедитесь в наличии достаточного количества кредитов и проверьте, что доступ к Realtime API включён. Посетите <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">документацию OpenAI</a> для получения дополнительной помощи.',
    },
  },
  de: {
    pageTitle: 'OpenAI-Einrichtungstutorial',
    backLink: 'Zurück zu KI-Anbietern',
    overview: {
      title: 'Übersicht',
      content: 'Diese Anleitung hilft Ihnen bei der Einrichtung von OpenAI für die Verwendung mit Sokuji. OpenAI bietet leistungsstarke KI-Modelle, einschließlich der Realtime API für Echtzeit-Sprachübersetzung.',
    },
    requirements: {
      title: 'Voraussetzungen',
      items: [
        'OpenAI-Konto (erstellen Sie eines auf platform.openai.com)',
        'Zahlungsmethode in Ihrem OpenAI-Konto hinterlegt',
        'API-Guthaben (mindestens $5 empfohlen)',
        'Zugang zur Realtime API (bei kostenpflichtigen Konten enthalten)',
      ],
    },
    apiKeyVerifier: {
      title: 'API-Schlüssel-Überprüfungstool',
      description: 'Fügen Sie Ihren OpenAI-API-Schlüssel unten ein, um zu überprüfen, ob er die erforderlichen Berechtigungen für Sokuji hat.',
      inputLabel: 'OpenAI-API-Schlüssel',
      inputPlaceholder: 'sk-...',
      verifyButton: 'API-Schlüssel überprüfen',
      clearButton: 'Löschen',
      verifying: 'Überprüfung...',
      results: {
        title: 'Überprüfungsergebnisse',
        valid: 'API-Schlüssel ist gültig',
        invalid: 'API-Schlüssel ist ungültig',
        realtimeAccess: 'Zugang zur Realtime API',
        whisperAccess: 'Zugang zur Whisper API',
        checking: 'Überprüfung...',
      },
      summary: {
        ready: 'Ihr API-Schlüssel ist bereit für die Verwendung mit Sokuji!',
        notReady: 'Ihr API-Schlüssel benötigt zusätzliche Berechtigungen oder Guthaben.',
      },
    },
    steps: {
      createAccount: {
        title: 'Schritt 1: OpenAI-Konto erstellen',
        content: 'Gehen Sie zu <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> und registrieren Sie sich für ein Konto. Sie können Ihre E-Mail-Adresse verwenden oder sich mit Google/Microsoft anmelden.',
      },
      getApiKey: {
        title: 'Schritt 2: API-Schlüssel generieren',
        content: 'Navigieren Sie zu <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a> in Ihrem OpenAI-Dashboard. Klicken Sie auf „Neuen geheimen Schlüssel erstellen", vergeben Sie einen Namen (z. B. „Sokuji") und kopieren Sie den Schlüssel. Speichern Sie ihn sicher, da Sie ihn nicht erneut sehen können.',
      },
      addCredits: {
        title: 'Schritt 3: Guthaben aufladen',
        content: 'Gehen Sie zu <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">Abrechnung</a> und fügen Sie eine Zahlungsmethode hinzu. Wir empfehlen, mindestens $5-10 Guthaben zum Start aufzuladen. Die Realtime API kostet etwa $0,06 pro Minute Audio.',
      },
      configureInApp: {
        title: 'Schritt 4: In Sokuji konfigurieren',
        content: 'Öffnen Sie Sokuji und gehen Sie zu den Einstellungen. Wählen Sie „OpenAI" als Ihren KI-Anbieter und fügen Sie Ihren API-Schlüssel in das entsprechende Feld ein. Klicken Sie auf „Überprüfen", um sicherzustellen, dass der Schlüssel korrekt funktioniert.',
      },
    },
    tips: {
      title: 'Tipps für beste Ergebnisse',
      items: [
        'Überwachen Sie Ihre Nutzung im OpenAI-Dashboard, um unerwartete Kosten zu vermeiden',
        'Richten Sie Nutzungslimits in Ihren OpenAI-Kontoeinstellungen ein',
        'Die Realtime API bietet die beste Qualität für Echtzeit-Übersetzung',
        'Halten Sie Ihren API-Schlüssel sicher und teilen Sie ihn niemals öffentlich',
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content: 'Wenn Probleme auftreten, überprüfen Sie die Berechtigungen Ihres API-Schlüssels, stellen Sie sicher, dass genügend Guthaben vorhanden ist, und vergewissern Sie sich, dass der Zugang zur Realtime API aktiviert ist. Besuchen Sie die <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">OpenAI-Dokumentation</a> für weitere Hilfe.',
    },
  },
  uk: {
    pageTitle: 'Посібник з налаштування OpenAI',
    backLink: 'Назад до провайдерів ШІ',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати OpenAI для використання з Sokuji. OpenAI надає потужні моделі ШІ, включаючи Realtime API для голосового перекладу в реальному часі.',
    },
    requirements: {
      title: 'Вимоги',
      items: [
        'Обліковий запис OpenAI (створіть на platform.openai.com)',
        'Спосіб оплати, доданий до вашого облікового запису OpenAI',
        'Кредити API (рекомендовано мінімум $5)',
        'Доступ до Realtime API (включено в платні облікові записи)',
      ],
    },
    apiKeyVerifier: {
      title: 'Інструмент перевірки API-ключа',
      description: 'Вставте ваш API-ключ OpenAI нижче, щоб перевірити наявність необхідних дозволів для Sokuji.',
      inputLabel: 'API-ключ OpenAI',
      inputPlaceholder: 'sk-...',
      verifyButton: 'Перевірити API-ключ',
      clearButton: 'Очистити',
      verifying: 'Перевірка...',
      results: {
        title: 'Результати перевірки',
        valid: 'API-ключ дійсний',
        invalid: 'API-ключ недійсний',
        realtimeAccess: 'Доступ до Realtime API',
        whisperAccess: 'Доступ до Whisper API',
        checking: 'Перевірка...',
      },
      summary: {
        ready: 'Ваш API-ключ готовий до використання з Sokuji!',
        notReady: 'Вашому API-ключу потрібні додаткові дозволи або кредити.',
      },
    },
    steps: {
      createAccount: {
        title: 'Крок 1: Створіть обліковий запис OpenAI',
        content: 'Перейдіть на <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> та зареєструйте обліковий запис. Ви можете використати електронну пошту або увійти через Google/Microsoft.',
      },
      getApiKey: {
        title: 'Крок 2: Згенеруйте API-ключ',
        content: 'Перейдіть до <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a> у панелі керування OpenAI. Натисніть «Створити новий секретний ключ», дайте йому назву (наприклад, «Sokuji») та скопіюйте ключ. Обов\'язково збережіть його в безпечному місці, оскільки ви не зможете побачити його знову.',
      },
      addCredits: {
        title: 'Крок 3: Поповніть баланс',
        content: 'Перейдіть до <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">Білінг</a> та додайте спосіб оплати. Рекомендуємо поповнити баланс мінімум на $5-10. Вартість Realtime API складає приблизно $0,06 за хвилину аудіо.',
      },
      configureInApp: {
        title: 'Крок 4: Налаштування в Sokuji',
        content: 'Відкрийте Sokuji та перейдіть до налаштувань. Виберіть «OpenAI» як провайдера ШІ та вставте API-ключ у відповідне поле. Натисніть «Перевірити», щоб переконатися, що ключ працює коректно.',
      },
    },
    tips: {
      title: 'Поради для кращих результатів',
      items: [
        'Відстежуйте використання в панелі керування OpenAI, щоб уникнути неочікуваних витрат',
        'Встановіть ліміти використання в налаштуваннях облікового запису OpenAI',
        'Realtime API забезпечує найкращу якість перекладу в реальному часі',
        'Зберігайте API-ключ у безпеці та ніколи не діліться ним публічно',
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      content: 'Якщо виникли проблеми, перевірте дозволи API-ключа, переконайтеся у наявності достатнього кредиту та перевірте, що доступ до Realtime API увімкнено. Відвідайте <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">документацію OpenAI</a> для отримання додаткової допомоги.',
    },
  },
  it: {
    pageTitle: 'Tutorial di configurazione OpenAI',
    backLink: 'Torna ai fornitori di IA',
    overview: {
      title: 'Panoramica',
      content: 'Questa guida ti aiuterà a configurare OpenAI per l\'uso con Sokuji. OpenAI offre potenti modelli di IA, inclusa la Realtime API per la traduzione vocale in tempo reale.',
    },
    requirements: {
      title: 'Requisiti',
      items: [
        'Account OpenAI (creane uno su platform.openai.com)',
        'Metodo di pagamento aggiunto al tuo account OpenAI',
        'Crediti API (minimo $5 consigliati)',
        'Accesso alla Realtime API (incluso con gli account a pagamento)',
      ],
    },
    apiKeyVerifier: {
      title: 'Strumento di verifica della chiave API',
      description: 'Incolla la tua chiave API OpenAI qui sotto per verificare che abbia i permessi necessari per Sokuji.',
      inputLabel: 'Chiave API OpenAI',
      inputPlaceholder: 'sk-...',
      verifyButton: 'Verifica chiave API',
      clearButton: 'Cancella',
      verifying: 'Verifica in corso...',
      results: {
        title: 'Risultati della verifica',
        valid: 'La chiave API è valida',
        invalid: 'La chiave API non è valida',
        realtimeAccess: 'Accesso alla Realtime API',
        whisperAccess: 'Accesso alla Whisper API',
        checking: 'Verifica in corso...',
      },
      summary: {
        ready: 'La tua chiave API è pronta per l\'uso con Sokuji!',
        notReady: 'La tua chiave API necessita di permessi o crediti aggiuntivi.',
      },
    },
    steps: {
      createAccount: {
        title: 'Passaggio 1: Creare un account OpenAI',
        content: 'Vai su <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer">platform.openai.com</a> e registrati per un account. Puoi usare la tua e-mail o accedere con Google/Microsoft.',
      },
      getApiKey: {
        title: 'Passaggio 2: Generare una chiave API',
        content: 'Naviga verso <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">API Keys</a> nella tua dashboard OpenAI. Clicca su "Crea nuova chiave segreta", assegna un nome (es. "Sokuji") e copia la chiave. Assicurati di salvarla in modo sicuro perché non potrai rivederla.',
      },
      addCredits: {
        title: 'Passaggio 3: Aggiungere crediti al tuo account',
        content: 'Vai su <a href="https://platform.openai.com/account/billing" target="_blank" rel="noopener noreferrer">Fatturazione</a> e aggiungi un metodo di pagamento. Consigliamo di aggiungere almeno $5-10 di crediti per iniziare. La Realtime API costa circa $0,06 al minuto di audio.',
      },
      configureInApp: {
        title: 'Passaggio 4: Configurare in Sokuji',
        content: 'Apri Sokuji e vai alle impostazioni. Seleziona "OpenAI" come fornitore di IA e incolla la tua chiave API nel campo corrispondente. Clicca su "Verifica" per assicurarti che la chiave funzioni correttamente.',
      },
    },
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Monitora il tuo utilizzo nella dashboard OpenAI per evitare addebiti imprevisti',
        'Imposta limiti di utilizzo nelle impostazioni del tuo account OpenAI',
        'La Realtime API offre la migliore qualità per la traduzione in tempo reale',
        'Mantieni la tua chiave API al sicuro e non condividerla mai pubblicamente',
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content: 'Se riscontri problemi, verifica i permessi della tua chiave API, assicurati di avere crediti sufficienti e verifica che l\'accesso alla Realtime API sia abilitato. Visita la <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">documentazione OpenAI</a> per ulteriore assistenza.',
    },
  },
};

export function OpenAISetup() {
  const { locale } = useI18n();
  const t = translations[locale] || translations.en;
  useDocumentMeta(t.pageTitle, t.overview.content);

  const [apiKey, setApiKey] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    valid: boolean | null;
    realtime: boolean | null;
    whisper: boolean | null;
  } | null>(null);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  const handleVerify = async () => {
    if (!apiKey.trim()) return;

    setVerifying(true);
    setVerificationResult({ valid: null, realtime: null, whisper: null });

    try {
      // Check API key validity with a simple models list call
      const modelsResponse = await fetch('https://api.openai.com/v1/models', {
        headers: {
          Authorization: `Bearer ${apiKey}`,
        },
      });

      const isValid = modelsResponse.ok;
      setVerificationResult((prev) => ({ ...prev!, valid: isValid }));

      if (isValid) {
        // Check for realtime access - look for gpt-4o-realtime models
        const modelsData = await modelsResponse.json();
        const hasRealtime = modelsData.data?.some((model: { id: string }) =>
          model.id.includes('realtime')
        );
        setVerificationResult((prev) => ({ ...prev!, realtime: hasRealtime }));

        // Check for whisper access
        const hasWhisper = modelsData.data?.some((model: { id: string }) =>
          model.id.includes('whisper')
        );
        setVerificationResult((prev) => ({ ...prev!, whisper: hasWhisper }));
      } else {
        setVerificationResult({ valid: false, realtime: false, whisper: false });
      }
    } catch {
      setVerificationResult({ valid: false, realtime: false, whisper: false });
    } finally {
      setVerifying(false);
    }
  };

  const handleClear = () => {
    setApiKey('');
    setVerificationResult(null);
  };

  const renderResultIcon = (value: boolean | null) => {
    if (value === null) return <Loader2 size={16} className="animate-spin" />;
    return value ? (
      <CheckCircle size={16} className="text-green-500" />
    ) : (
      <XCircle size={16} className="text-red-500" />
    );
  };

  return (
    <div className="docs-content tutorial-page">
      <Link to={localizePath('/docs/ai-providers', locale)} className="tutorial-page__back-link">
        <ArrowLeft size={16} />
        {t.backLink}
      </Link>

      <h1>{t.pageTitle}</h1>

      {/* Overview */}
      <section className="tutorial-page__section">
        <h2>{t.overview.title}</h2>
        <p>{t.overview.content}</p>
      </section>

      {/* Requirements */}
      <section className="tutorial-page__section">
        <h2>{t.requirements.title}</h2>
        <ul>
          {t.requirements.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </section>

      {/* API Key Verifier */}
      <section className="tutorial-page__section">
        <h2>{t.apiKeyVerifier.title}</h2>
        <p>{t.apiKeyVerifier.description}</p>

        <div className="api-key-verifier">
          <div className="api-key-verifier__input-group">
            <label htmlFor="api-key">{t.apiKeyVerifier.inputLabel}</label>
            <input
              id="api-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={t.apiKeyVerifier.inputPlaceholder}
            />
          </div>

          <div className="api-key-verifier__buttons">
            <button
              className="api-key-verifier__btn api-key-verifier__btn--primary"
              onClick={handleVerify}
              disabled={verifying || !apiKey.trim()}
            >
              {verifying ? t.apiKeyVerifier.verifying : t.apiKeyVerifier.verifyButton}
            </button>
            <button className="api-key-verifier__btn api-key-verifier__btn--secondary" onClick={handleClear}>
              {t.apiKeyVerifier.clearButton}
            </button>
          </div>

          {verificationResult && (
            <div className="api-key-verifier__results">
              <h4>{t.apiKeyVerifier.results.title}</h4>
              <div
                className={`api-key-verifier__result ${
                  verificationResult.valid === null
                    ? 'api-key-verifier__result--loading'
                    : verificationResult.valid
                    ? 'api-key-verifier__result--success'
                    : 'api-key-verifier__result--error'
                }`}
              >
                {renderResultIcon(verificationResult.valid)}{' '}
                {verificationResult.valid === null
                  ? t.apiKeyVerifier.results.checking
                  : verificationResult.valid
                  ? t.apiKeyVerifier.results.valid
                  : t.apiKeyVerifier.results.invalid}
              </div>

              {verificationResult.valid && (
                <>
                  <div
                    className={`api-key-verifier__result ${
                      verificationResult.realtime === null
                        ? 'api-key-verifier__result--loading'
                        : verificationResult.realtime
                        ? 'api-key-verifier__result--success'
                        : 'api-key-verifier__result--error'
                    }`}
                  >
                    {renderResultIcon(verificationResult.realtime)} {t.apiKeyVerifier.results.realtimeAccess}
                  </div>
                  <div
                    className={`api-key-verifier__result ${
                      verificationResult.whisper === null
                        ? 'api-key-verifier__result--loading'
                        : verificationResult.whisper
                        ? 'api-key-verifier__result--success'
                        : 'api-key-verifier__result--error'
                    }`}
                  >
                    {renderResultIcon(verificationResult.whisper)} {t.apiKeyVerifier.results.whisperAccess}
                  </div>
                </>
              )}

              {verificationResult.valid !== null && (
                <div
                  className={`api-key-verifier__summary ${
                    verificationResult.valid && verificationResult.realtime
                      ? 'api-key-verifier__summary--success'
                      : 'api-key-verifier__summary--error'
                  }`}
                >
                  {verificationResult.valid && verificationResult.realtime
                    ? t.apiKeyVerifier.summary.ready
                    : t.apiKeyVerifier.summary.notReady}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Steps */}
      <section className="tutorial-page__section">
        <div className="tutorial-page__step">
          <h3>
            <span className="tutorial-page__step-number">1</span>
            {t.steps.createAccount.title.replace('Step 1: ', '').replace('步骤 1：', '').replace('ステップ 1: ', '').replace('1단계: ', '').replace('Paso 1: ', '').replace('Étape 1 : ', '').replace('الخطوة 1: ', '').replace('Passo 1: ', '').replace('Шаг 1: ', '').replace('Schritt 1: ', '').replace('Крок 1: ', '').replace('Passaggio 1: ', '')}
          </h3>
          <p dangerouslySetInnerHTML={{ __html: t.steps.createAccount.content }} />
          <img
            src="/tutorials/openai-setup/1.png"
            alt="OpenAI Sign Up"
            className="tutorial-page__screenshot"
            onClick={() => setLightboxImage({ src: '/tutorials/openai-setup/1.png', alt: 'OpenAI Sign Up' })}
          />
        </div>

        <div className="tutorial-page__step">
          <h3>
            <span className="tutorial-page__step-number">2</span>
            {t.steps.getApiKey.title.replace('Step 2: ', '').replace('步骤 2：', '').replace('ステップ 2: ', '').replace('2단계: ', '').replace('Paso 2: ', '').replace('Étape 2 : ', '').replace('الخطوة 2: ', '').replace('Passo 2: ', '').replace('Шаг 2: ', '').replace('Schritt 2: ', '').replace('Крок 2: ', '').replace('Passaggio 2: ', '')}
          </h3>
          <p dangerouslySetInnerHTML={{ __html: t.steps.getApiKey.content }} />
          <img
            src="/tutorials/openai-setup/2.png"
            alt="Generate API Key"
            className="tutorial-page__screenshot"
            onClick={() => setLightboxImage({ src: '/tutorials/openai-setup/2.png', alt: 'Generate API Key' })}
          />
        </div>

        <div className="tutorial-page__step">
          <h3>
            <span className="tutorial-page__step-number">3</span>
            {t.steps.addCredits.title.replace('Step 3: ', '').replace('步骤 3：', '').replace('ステップ 3: ', '').replace('3단계: ', '').replace('Paso 3: ', '').replace('Étape 3 : ', '').replace('الخطوة 3: ', '').replace('Passo 3: ', '').replace('Шаг 3: ', '').replace('Schritt 3: ', '').replace('Крок 3: ', '').replace('Passaggio 3: ', '')}
          </h3>
          <p dangerouslySetInnerHTML={{ __html: t.steps.addCredits.content }} />
          <img
            src="/tutorials/openai-setup/credit-balance.png"
            alt="Add Credits"
            className="tutorial-page__screenshot"
            onClick={() =>
              setLightboxImage({ src: '/tutorials/openai-setup/credit-balance.png', alt: 'Add Credits' })
            }
          />
        </div>

        <div className="tutorial-page__step">
          <h3>
            <span className="tutorial-page__step-number">4</span>
            {t.steps.configureInApp.title.replace('Step 4: ', '').replace('步骤 4：', '').replace('ステップ 4: ', '').replace('4단계: ', '').replace('Paso 4: ', '').replace('Étape 4 : ', '').replace('الخطوة 4: ', '').replace('Passo 4: ', '').replace('Шаг 4: ', '').replace('Schritt 4: ', '').replace('Крок 4: ', '').replace('Passaggio 4: ', '')}
          </h3>
          <p dangerouslySetInnerHTML={{ __html: t.steps.configureInApp.content }} />
        </div>
      </section>

      {/* Tips */}
      <section className="tutorial-page__section">
        <div className="tutorial-page__tip">
          <h4>{t.tips.title}</h4>
          <ul>
            {t.tips.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="tutorial-page__section">
        <h2>{t.troubleshooting.title}</h2>
        <p dangerouslySetInnerHTML={{ __html: t.troubleshooting.content }} />
      </section>

      {/* External Link */}
      <section className="tutorial-page__section">
        <a
          href="https://platform.openai.com"
          target="_blank"
          rel="noopener noreferrer"
          className="install-page__download-btn"
        >
          OpenAI Platform
          <ExternalLink size={16} />
        </a>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          isOpen={!!lightboxImage}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
}
